/**
 * Small wrapper around the Google Analytics 4 gtag.js runtime.
 *
 * The gtag snippet + Consent Mode v2 defaults live in `public/index.html`
 * so they load *before* React hydrates. This module only exposes helpers
 * that React components can call safely (no-ops when gtag is missing).
 */

export const GA4_ID = "G-KZ8Y19CMLM";
export const CONSENT_STORAGE_KEY = "ds_cookie_consent";

const isBrowser = () => typeof window !== "undefined";

/**
 * Update Google Consent Mode v2 signals.
 * Accepts "all" (accept everything) or "essential" (deny analytics + ads).
 */
export function applyConsent(choice) {
  if (!isBrowser() || typeof window.gtag !== "function") return;
  const granted = choice === "all";
  window.gtag("consent", "update", {
    ad_storage: granted ? "granted" : "denied",
    analytics_storage: granted ? "granted" : "denied",
    ad_user_data: granted ? "granted" : "denied",
    ad_personalization: granted ? "granted" : "denied",
  });
  // Consent-gated third-party trackers (Clixtell + PostHog). Both loaders
  // are defined in public/index.html as idempotent no-ops until called.
  // Google Consent Mode above is unchanged; these run only on full accept.
  if (granted) {
    if (typeof window.__dsLoadClixtell === "function") window.__dsLoadClixtell();
    if (typeof window.__dsLoadPostHog === "function") window.__dsLoadPostHog();
  }
}

/**
 * Fire a single page_view event for the current route.
 * We rely on `page_path`/`page_location`/`page_title` so GA4 receives the
 * post-navigation URL and title instead of the URL that was active when
 * gtag.js first loaded.
 *
 * No form values, patient names, phone numbers, or medical data are ever
 * passed here — only the URL and the document title.
 */
export function trackPageView({ path, title } = {}) {
  if (!isBrowser() || typeof window.gtag !== "function") return;
  const pagePath = path || window.location.pathname + window.location.search;
  const pageTitle = title || document.title;
  window.gtag("event", "page_view", {
    page_path: pagePath,
    page_location: window.location.origin + pagePath,
    page_title: pageTitle,
    send_to: GA4_ID,
  });
}

/**
 * Generic GA4 custom-event dispatcher.
 *
 * Used for the clinic's four conversion events: phone_click, whatsapp_click,
 * generate_lead, directions_click. Sends to the currently-registered gtag
 * runtime, which already has both G-KZ8Y19CMLM (GA4) and AW-861500279
 * (Google Ads) configured. GA4 will record the event; Google Ads picks it
 * up automatically through the linked GA4 property once individual
 * conversion-action labels are imported in a later phase.
 *
 * IMPORTANT: never pass patient names, phone numbers, form contents, or
 * medical data — only click metadata (link URL, short link text, page path)
 * and, for form submissions, non-identifying selections such as the chosen
 * service and contact preference.
 */
export function trackEvent(name, params = {}) {
  if (!isBrowser() || typeof window.gtag !== "function") return;
  window.gtag("event", name, params);
}
